import React, {useContext, useEffect, useState} from "react";
import {Language} from "../../util/enum";
import TemplateEditor, {defaultAvailableMentionKeys, MentionKey, ReplaceableInfo} from "./templateEditor";
import Button from "../../components/button/button";
import {ButtonStatus} from "../../components/button/enum";
import {CampaignSetting} from "./dataForDesignCampaign";
import {AutomatedCampaignType, CampaignBigType} from "./dataForActiveCampaign";
import {ProviderContext} from "../providerContext";

export interface TemplateContent {
    language: Language
    id: string | number,
    compliedMessage: string,
    libMessage: string,
    name: string,
    maxlength: number
}

interface Template {
    invalidCompileMessage: string,
    content: TemplateContent,
}

interface IProps {
    title?: string,
    setting: CampaignSetting,
    isDataValid: boolean,
    data: Array<TemplateContent>,
    replaceableInfo?: ReplaceableInfo,
    onRemove: (language: Language) => void
    onChange: (libMessage: string, compliedMessage: string) => void
    onFinish: (libMessage: string, compliedMessage: string, language: Language) => void
}

function TemplateEditContentList(props: IProps) {
    const {title, setting, isDataValid, data, replaceableInfo, onChange, onFinish} = props
    const [showAll, setShowAll] = useState<boolean>(true)
    const [isFocused, setIsFocused] = useState<boolean>(false)
    const [focusStatus, setFocusStatus] = useState<Map<number, boolean>>(new Map<number, boolean>())
    const [invalidStatus, setInvalidStatus] = useState<Map<string, string>>(new Map<string, string>())

    const [templates, setTemplates] = useState<Array<Template>>([])

    const provider = useContext(ProviderContext)

    useEffect(() => {
        const ts = data.map((item) => {
            return {
                invalidCompileMessage: invalidStatus.get(item.language) || "",
                content: item,
            }
        })
        setTemplates(ts)
    }, [data])

    // useEffect(() => {
    //     if (setting.campaignBigType !== CampaignBigType.automatedCampaign && data.length) {
    //         let nonEnglishTemplate = data.find((item) => item.compliedMessage.length > 0 && item.language !== Language.english)
    //         setShowAll(nonEnglishTemplate !== undefined)
    //     }
    // }, [data, setting])

    const $actionFooter = (name: string) => {
        return (
            <div className={"flex flex-row items-center justify-center my-2.5"}>
                <p className={"text-sm text-gray-2"}>{name}</p>
            </div>
        )
    }

    const $generateOtherTemplateButton = (!showAll ? (<Button status={ButtonStatus.link} onClick={() => {
        setShowAll(true)
    }}>Write messages in other languages</Button>) : null)

    const $readOnlyFooter = (language: string) => {
        return (
            <div>
                <div className={"text-right"}>
                    <p className={"inline-flex text-sm text-gray-2 my-2.5"}>
                        <span className={"text-sm text-gray-500 mr-1"}>{"Default:"}</span>
                        {language}
                    </p>
                </div>
                {$generateOtherTemplateButton}
            </div>
        )
    }

    let availableMentionKeys = defaultAvailableMentionKeys
    if (setting.campaignBigType === CampaignBigType.regularCampaign) {
        availableMentionKeys = replaceableInfo?.officePhone
            ? [MentionKey.patientName, MentionKey.practiceName, MentionKey.officePhone, MentionKey.appointmentLink]
            : [MentionKey.patientName, MentionKey.practiceName, MentionKey.appointmentLink]
    } else if (setting.automatedCampaignType === AutomatedCampaignType.birthday) {
        availableMentionKeys = [MentionKey.practiceName, MentionKey.providerName, MentionKey.patientFirstName]
    }

    const $errMsgForTemplateContent = (language: string) => {
        const compliedMessage = invalidStatus.get(language) || ""
        let invalidMsg = ""
        if (!isDataValid && language === Language.english && !compliedMessage.length) {
            invalidMsg = "Message content is required."
        } else if (compliedMessage.includes(MentionKey.appointmentLink) && !provider.clinicOnlineScheduleEnable) {
            invalidMsg = "The text-to-book feature is disabled because you haven't enable the provider portal. Please contact iClinicHealth team at digitalhealth@mdland.com"
        }
        return invalidMsg ? <p className="text-red-500 text-xs italic my-1">{invalidMsg}</p> : <div/>
    }
    const $templateItem = ({libMessage, language, maxlength}: TemplateContent, invalidCompileMessage: string, idx: number) => {
        return (
            <div key={idx} className={`${showAll ? "border-b border-gray-200 mb-2.5" : ""}`}>
                <TemplateEditor
                    maxlength={maxlength}
                    libMessage={libMessage}
                    availableMentionKeys={availableMentionKeys}
                    replaceableInfo={replaceableInfo}
                    onEditorFocus={() => {
                        focusStatus.set(idx, true)
                        setFocusStatus(focusStatus)
                        setIsFocused(true)
                    }}
                    onEditorChange={(libMessage, compliedMessage) => {
                        if (compliedMessage !== invalidStatus.get(language)) {
                            invalidStatus.set(language, compliedMessage)
                            setInvalidStatus(invalidStatus)
                            const ts = templates.map((item) => {
                                if (item.content.language === language) {
                                    item.invalidCompileMessage = compliedMessage
                                }
                                return item
                            })
                            setTemplates(ts)
                            onChange && onChange(libMessage, compliedMessage)
                        }
                    }}
                    onEditorBlur={(libMessage, compliedMessage) => {
                        focusStatus.set(idx, false)
                        setFocusStatus(focusStatus)
                        setIsFocused((Array.from(focusStatus.values())).includes(true))
                        onFinish && onFinish(libMessage, compliedMessage, language)
                    }}
                />

                <div className={"w-full flex items-center justify-between flex-row mb-2"}>
                    {$errMsgForTemplateContent(language)}
                    {language === Language.english ? $readOnlyFooter(language) : $actionFooter(language)}
                </div>
            </div>
        )
    }

    const $title = (
        <p className={"relative font-semibold text-gray-1 text-sm text-left mb-1"}>{title}</p>
    )
    const $subTitle = (
        <p className={"relative font-semibold text-gray-3 text-sm text-left mb-1"}>
            Use {<span className={"text-primary"}>#</span>} to insert names or links.
        </p>
    )

    const dataSource = showAll ? templates : templates.slice(0, 1)
    return (
        <div>
            {$title}
            {$subTitle}
            <div className={`w-full border ${isFocused ? "border-theme-blue ring-4 ring-color-theme" : ""} rounded p-5`}>
                {dataSource.map(({content, invalidCompileMessage}, idx) => {
                    return $templateItem(content, invalidCompileMessage, idx)
                })}
            </div>
        </div>
    )
}

export default TemplateEditContentList
